import { TractNRI, SubtractNRI } from 'components/nri-table/types';
import { SlotVerificationCounts, NriVerificationCounts } from './types';

type UnitDevelopmentType = 'horizontal' | 'vertical';

// Filters slots by unitDevelopmentType. "directional" is treated as "vertical".
function filterByUnitType(
  slots: TractNRI[],
  unitDevelopmentType: UnitDevelopmentType
): TractNRI[] {
  return slots.filter((slot) => {
    const type = slot.unitDevelopmentType?.toLowerCase();
    if (unitDevelopmentType === 'vertical') {
      return type === 'vertical' || type === 'directional';
    }
    return type === unitDevelopmentType.toLowerCase();
  });
}

// Calculates slot-level verification counts.
// Groups slots by their verification category based on their subtracts' statuses.
export function calculateSlotVerificationCounts(
  slots: TractNRI[],
  unitDevelopmentType: UnitDevelopmentType,
  _getStatus: (subtract: SubtractNRI) => boolean | null
): SlotVerificationCounts {
  const filteredSlots = filterByUnitType(slots, unitDevelopmentType);

  const counts = filteredSlots.reduce(
    (acc, slot) => {
      // Only certified slots count as verified; all others are notStarted
      // (old subtract-level verification status is ignored)
      const category =
        slot.wiSource === 'certified' ? 'verified' : 'notStarted';
      return { ...acc, [category]: acc[category] + 1 };
    },
    { verified: 0, inProgress: 0, notStarted: 0, incorrect: 0 }
  );

  return {
    total: filteredSlots.length,
    verified: counts.verified,
    inProgress: counts.inProgress,
    notStarted: counts.notStarted,
    incorrect: counts.incorrect,
  };
}

// Calculates NRI-level (subtract-level) verification counts.
// Counts individual NRI calculations by their verification status.
export function calculateNriVerificationCounts(
  slots: TractNRI[],
  unitDevelopmentType: UnitDevelopmentType,
  getStatus: (subtract: SubtractNRI) => boolean | null
): NriVerificationCounts {
  const filteredSlots = filterByUnitType(slots, unitDevelopmentType);

  const counts = filteredSlots
    .flatMap((slot) => slot.subtracts ?? [])
    .reduce(
      (acc, subtract) => {
        const status = getStatus(subtract);
        if (status === true) return { ...acc, verified: acc.verified + 1 };
        if (status === false) return { ...acc, incorrect: acc.incorrect + 1 };
        return { ...acc, notStarted: acc.notStarted + 1 };
      },
      { verified: 0, notStarted: 0, incorrect: 0 }
    );

  return {
    total: counts.verified + counts.notStarted + counts.incorrect,
    verified: counts.verified,
    notStarted: counts.notStarted,
    incorrect: counts.incorrect,
  };
}
